/* ==========================================================================
   bb-cart-plus.js: bag additions from the 2026-10-12 shoppability audit.
   Loaded (deferred) by snippets/cart-drawer.liquid right after bb-cart.js.
   Vanilla, no dependencies. Every behavior is a custom element so it
   re-initializes after the theme's AJAX section swaps, same pattern as
   bb-cart.js.

     <bb-cart-gap>        Add buttons on the gap-closer row (snippets/bb-cart-gap)
     <bb-cart-note>       "Add a gift note" toggle, saves cart.note (snippets/bb-cart-note)
     <bb-cart-page-gift>  gift rules on /cart (sections/main-cart): add, remove, one unit
     <bb-cart-sticky>     phone Checkout bar on /cart while the real button is off screen

   Element names, ids and classes that Massy or bb-cart.js read are untouched.
   ========================================================================== */
(function () {
  'use strict';

  var JSON_HEADERS = { 'Content-Type': 'application/json', 'Accept': 'application/json' };

  function setBubbles(count) {
    if (typeof count !== 'number') return;
    document.querySelectorAll('.cart-icon-bubble-count').forEach(function (el) { el.textContent = count; });
    /* the bag icon's spoken name ("Bag, 3 items", snippets/cart.liquid), set from the
       count itself so it is right even where no count bubble is on the page yet */
    document.querySelectorAll('#cart-link a[data-bag-name]').forEach(function (a) {
      var name = a.getAttribute('data-bag-name') || 'Bag';
      a.setAttribute('aria-label', count > 0 ? name + ', ' + count + ' ' + (count === 1 ? 'item' : 'items') : name);
    });
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) drawerEl.classList.toggle('is-empty', count === 0);
  }

  /* Same drawer re-render bb-cart.js uses: fresh .drawer__inner from the
     Section Rendering API, then tell the page (Massy listens for cart:updated). */
  function refreshDrawer(cart) {
    /* no Accept: application/json header here: on a product URL that header makes
       Shopify answer with the product JSON instead of the sections payload */
    return fetch(window.location.pathname + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        var html = sections && sections['cart-drawer'];
        if (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('.drawer__inner');
          var current = document.querySelector('#CartDrawer .drawer__inner');
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        }
        if (cart) setBubbles(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'bb-cart-plus' } }));
      })
      .catch(function (e) { console.error('[bb-cart-plus] drawer refresh failed', e); });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /* ---------- gap closer: one tap adds the 1 Piece size ---------- */
  if (!customElements.get('bb-cart-gap')) {
    customElements.define('bb-cart-gap', class extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('.bbc-gap__add').forEach(function (btn) {
          if (btn.dataset.bound) return;
          btn.dataset.bound = '1';
          btn.addEventListener('click', function () {
            if (btn.disabled) return;
            var label = btn.textContent;
            btn.disabled = true;
            btn.classList.add('is-busy');
            btn.textContent = 'Adding';
            fetch('/cart/add.js', {
              method: 'POST',
              headers: JSON_HEADERS,
              body: JSON.stringify({ id: Number(btn.dataset.variant), quantity: 1 })
            }).then(function (r) {
              if (!r.ok) throw new Error('add.js ' + r.status);
              return getCart();
            }).then(function (cart) {
              btn.textContent = 'Added';
              return refreshDrawer(cart);
            }).catch(function (e) {
              console.error('[bb-cart-plus] gap add failed', e);
              btn.disabled = false;
              btn.classList.remove('is-busy');
              btn.textContent = label;
            });
          });
        });
      }
    });
  }

  /* ---------- gift note: native cart note, saved as you type ---------- */
  if (!customElements.get('bb-cart-note')) {
    customElements.define('bb-cart-note', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var toggle = self.querySelector('.bbc-note__toggle');
        var panel = self.querySelector('.bbc-note__panel');
        var field = self.querySelector('.bbc-note__field');
        var status = self.querySelector('.bbc-note__status');
        if (!toggle || !panel || !field || self._wired) return;
        self._wired = true;
        var saved = field.value;
        var timer = null;

        toggle.addEventListener('click', function () {
          var open = panel.hasAttribute('hidden');
          if (open) panel.removeAttribute('hidden'); else panel.setAttribute('hidden', '');
          toggle.setAttribute('aria-expanded', String(open));
          self.classList.toggle('is-open', open);
          if (open) { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }
        });

        function save() {
          var value = field.value;
          if (value === saved) return;
          if (status) status.textContent = 'Saving';
          fetch('/cart/update.js', { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify({ note: value }) })
            .then(function (r) { if (!r.ok) throw new Error('update.js ' + r.status); return r.json(); })
            .then(function () {
              saved = value;
              if (status) status.textContent = value ? 'Saved' : '';
              /* keep the other copy (drawer or /cart) in step without a re-render */
              document.querySelectorAll('bb-cart-note .bbc-note__field').forEach(function (other) {
                if (other !== field && document.activeElement !== other) other.value = value;
              });
            })
            .catch(function (e) { console.error('[bb-cart-plus] note save failed', e); if (status) status.textContent = 'Not saved, try again'; });
        }
        field.addEventListener('input', function () {
          if (timer) clearTimeout(timer);
          if (status) status.textContent = '';
          timer = setTimeout(save, 700);
        });
        field.addEventListener('blur', function () { if (timer) clearTimeout(timer); save(); });
      }
    });
  }

  /* ---------- /cart gift rules ----------
     The drawer's <bb-gift-watcher> (bb-cart.js) only runs when the drawer
     renders, and /cart quantity changes re-render main-cart only, so on /cart
     the gift could stay under $150 or be raised to 2 (audit CRT-02). This
     element lives inside main-cart, so it runs after every /cart change. It
     reads the live bag from /cart.js (never the possibly stale render), applies
     the same rule as the drawer (gift on at the threshold, off below it, one
     unit, never for wholesale or outside the US), then re-renders main-cart
     and the drawer. Capped at 6 corrections per page load so it can never loop. */
  function rerenderMainCart(sectionId) {
    var host = document.getElementById('main-cart');
    if (!host || !sectionId) return Promise.resolve();
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('main-cart');
        if (fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(function (e) { console.error('[bb-cart-plus] cart page refresh failed', e); });
  }

  if (!customElements.get('bb-cart-page-gift')) {
    customElements.define('bb-cart-page-gift', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var pid = Number(self.dataset.giftPid || 0);
        var variant = Number(self.dataset.variant || 0);
        if (!pid || !variant) return;
        var tries = 0;
        var run = function () {
          if (!self.isConnected) return;
          if (window.__bbGiftBusy && tries < 25) { tries++; setTimeout(run, 300); return; }
          getCart().then(function (cart) {
            var line = 0, qty = 0, rail = 0;
            (cart.items || []).forEach(function (it, i) {
              if (it.product_id === pid) { if (!line) { line = i + 1; qty = it.quantity; } }
              else rail += (typeof it.final_line_price === 'number' ? it.final_line_price : 0);
            });
            var eligible = self.dataset.allowed === 'true' && rail >= Number(self.dataset.threshold || 15000);
            var req = null;
            if (eligible && !line) req = { url: '/cart/add.js', body: { id: variant, quantity: 1 } };
            else if (!eligible && line) req = { url: '/cart/change.js', body: { line: line, quantity: 0 } };
            else if (line && qty > 1) req = { url: '/cart/change.js', body: { line: line, quantity: 1 } };

            var renderedHas = self.dataset.hasGift === 'true';
            var renderedQty = Number(self.dataset.giftQty || 0);
            if (!req) {
              /* nothing to fix, but the drawer watcher may have changed the bag
                 after this page was rendered: bring the page up to date once */
              if ((renderedHas !== !!line || (line && renderedQty !== qty)) && (window.__bbPageGiftRuns || 0) < 6) {
                window.__bbPageGiftRuns = (window.__bbPageGiftRuns || 0) + 1;
                rerenderMainCart(self.dataset.section);
              }
              return;
            }
            if ((window.__bbPageGiftRuns || 0) >= 6) return;
            window.__bbPageGiftRuns = (window.__bbPageGiftRuns || 0) + 1;
            window.__bbGiftBusy = true;
            fetch(req.url, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(req.body) })
              .then(function (r) { return r.json(); })
              .then(function () { return getCart(); })
              .then(function (fresh) {
                setBubbles(fresh.item_count);
                return Promise.all([rerenderMainCart(self.dataset.section), refreshDrawer(fresh)]);
              })
              .catch(function (e) { console.error('[bb-cart-plus] cart page gift rule failed', e); })
              .then(function () { window.__bbGiftBusy = false; });
          }).catch(function () {});
        };
        run();
      }
    });
  }

  /* ---------- /cart phone Checkout bar ----------
     On a phone the real Checkout button sits after every line (about 2,100px
     down a 7-line bag). This bar shows the same button at the bottom of the
     screen and hides itself whenever the real one is on screen, so there is
     only ever one Checkout in view. */
  if (!customElements.get('bb-cart-sticky')) {
    customElements.define('bb-cart-sticky', class extends HTMLElement {
      connectedCallback() {
        var self = this;
        var bar = self.querySelector('.bbc-sticky');
        if (!bar || !('IntersectionObserver' in window)) return;
        var mq = window.matchMedia('(max-width: 749px)');
        var realVisible = true;
        var update = function () {
          var show = mq.matches && !realVisible;
          bar.classList.toggle('is-shown', show);
          bar.setAttribute('aria-hidden', String(!show));
          var btn = bar.querySelector('button');
          if (btn) btn.tabIndex = show ? 0 : -1;
        };
        var target = document.getElementById('checkout');
        if (!target) return;
        if (self._io) self._io.disconnect();
        self._io = new IntersectionObserver(function (entries) {
          realVisible = entries.some(function (en) { return en.isIntersecting; });
          update();
        }, { threshold: 0.01 });
        self._io.observe(target);
        if (mq.addEventListener) mq.addEventListener('change', update);
        update();
      }
      disconnectedCallback() { if (this._io) this._io.disconnect(); }
    });
  }
})();
